import type { GitHunk } from "gitpod-next-api/supervisor/v1/environmentopts_pb";
import { useCallback, useMemo, useState, useEffect, type FC } from "react";
import { PatchDiff } from "@pierre/diffs/react";
import { registerCustomTheme, type SelectedLineRange, type DiffLineAnnotation } from "@pierre/diffs";
import { convertHunksToPatch } from "@/routes/environments/code-changes/diff-utils";
import { Button } from "@/components/flexkit/Button";
import type { ReviewComment } from "@/components/ai/review/types";
import pierreLightBase from "@/routes/environments/code-changes/themes/pierre-light.json";
import pierreDarkBase from "@/routes/environments/code-changes/themes/pierre-dark.json";
import { useTheme } from "@/hooks/use-theme";

// Surface primary colors from index.css
// Light: --surface-primary: var(--gray-200) = 246 246 246 = #f6f6f6
// Dark: --surface-primary: var(--gray-900) = 26 26 26 = #1a1a1a

registerCustomTheme("ona-light", () =>
    Promise.resolve({
        ...pierreLightBase,
        name: "ona-light",
        type: "light" as const,
        colors: {
            ...pierreLightBase.colors,
            "editor.background": "#f6f6f6",
        },
    }),
);

registerCustomTheme("ona-dark", () =>
    Promise.resolve({
        ...pierreDarkBase,
        name: "ona-dark",
        type: "dark" as const,
        colors: {
            ...pierreDarkBase.colors,
            "editor.background": "#1a1a1a",
        },
    }),
);

export type LineSelection = {
    startLine: number;
    endLine: number;
    startIndex: number;
    endIndex: number;
};

type AnnotationMetadata = {
    type: "pending-comment";
    startLine: number;
    endLine: number;
};

export type GitDiffViewProps = {
    hunks: GitHunk[];
    filePath?: string;
    selectable?: boolean;
    onLineSelect?: (selection: LineSelection) => void;
    comments?: ReviewComment[];
    onCommentClick?: (comment: ReviewComment) => void;
    pendingCommentSelection?: { startLine: number; endLine: number } | null;
    onAddComment?: (content: string) => void;
    onCancelComment?: () => void;
};

const GitDiffView: FC<GitDiffViewProps> = ({
    hunks,
    filePath = "file",
    selectable = false,
    onLineSelect,
    pendingCommentSelection,
    onAddComment,
    onCancelComment,
}) => {
    const { effectiveTheme } = useTheme();
    const patch = useMemo(() => convertHunksToPatch(hunks, filePath), [hunks, filePath]);
    const [inlineCommentContent, setInlineCommentContent] = useState("");

    // Reset inline comment content when selection changes
    useEffect(() => {
        setInlineCommentContent("");
    }, [pendingCommentSelection?.startLine, pendingCommentSelection?.endLine]);

    const handleLineSelectionEnd = useCallback(
        (range: SelectedLineRange | null) => {
            if (range && onLineSelect) {
                onLineSelect({
                    startLine: range.start,
                    endLine: range.end,
                    startIndex: range.start,
                    endIndex: range.end,
                });
            }
        },
        [onLineSelect],
    );

    const handleInlineCommentSubmit = useCallback(() => {
        if (inlineCommentContent.trim() && onAddComment) {
            onAddComment(inlineCommentContent.trim());
            setInlineCommentContent("");
            onCancelComment?.();
        }
    }, [inlineCommentContent, onAddComment, onCancelComment]);

    const handleInlineCommentCancel = useCallback(() => {
        setInlineCommentContent("");
        onCancelComment?.();
    }, [onCancelComment]);

    const handleInlineCommentKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleInlineCommentSubmit();
            } else if (e.key === "Escape") {
                handleInlineCommentCancel();
            }
        },
        [handleInlineCommentSubmit, handleInlineCommentCancel],
    );

    // Create line annotations for pending comment (only when selectable/review comments enabled)
    const lineAnnotations = useMemo<DiffLineAnnotation<AnnotationMetadata>[]>(() => {
        if (!selectable || !pendingCommentSelection) return [];
        // Add annotation at the end line of the selection (on additions side for unified view)
        return [
            {
                side: "additions",
                lineNumber: pendingCommentSelection.endLine,
                metadata: {
                    type: "pending-comment",
                    startLine: pendingCommentSelection.startLine,
                    endLine: pendingCommentSelection.endLine,
                },
            },
        ];
    }, [selectable, pendingCommentSelection]);

    const renderAnnotation = useCallback(
        (annotation: DiffLineAnnotation<AnnotationMetadata>) => {
            if (!annotation.metadata || annotation.metadata.type !== "pending-comment") return null;
            const { startLine, endLine } = annotation.metadata;
            return (
                <div className="border-t border-border-base bg-surface-secondary p-3">
                    <div className="mb-2 text-xs text-content-secondary">
                        {endLine !== startLine
                            ? `Comment on lines ${startLine}-${endLine}`
                            : `Comment on line ${startLine}`}
                    </div>
                    <textarea
                        value={inlineCommentContent}
                        onChange={(e) => setInlineCommentContent(e.target.value)}
                        onKeyDown={handleInlineCommentKeyDown}
                        placeholder="Add your comment..."
                        className="min-h-[60px] w-full resize-none rounded-md border border-border-base bg-surface-primary p-2 text-sm text-content-primary placeholder:text-content-muted focus:border-content-link focus:outline-none focus:ring-1 focus:ring-content-link"
                        rows={2}
                        autoFocus
                    />
                    <div className="mt-2 flex justify-end gap-2">
                        <Button
                            variant="secondary"
                            size="sm"
                            onClick={handleInlineCommentCancel}
                            data-tracking-id="cancel-inline-comment"
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleInlineCommentSubmit}
                            disabled={!inlineCommentContent.trim()}
                            data-tracking-id="add-inline-comment"
                        >
                            Add comment
                        </Button>
                    </div>
                </div>
            );
        },
        [inlineCommentContent, handleInlineCommentKeyDown, handleInlineCommentCancel, handleInlineCommentSubmit],
    );

    if (!patch) {
        return null;
    }

    return (
        <PatchDiff<AnnotationMetadata>
            patch={patch}
            options={{
                theme: { dark: "ona-dark", light: "ona-light" },
                themeType: effectiveTheme,
                diffStyle: "unified",
                diffIndicators: "bars",
                hunkSeparators: "simple",
                expandUnchanged: false,
                disableFileHeader: true,
                overflow: "scroll",
                enableLineSelection: selectable,
                onLineSelectionEnd: handleLineSelectionEnd,
            }}
            lineAnnotations={lineAnnotations}
            renderAnnotation={renderAnnotation}
            // Styling via CSS variables per https://diffs.com/docs#styling
            style={
                {
                    "--diffs-font-family": "var(--font-family-mono)",
                    "--diffs-gap-block": "0px",
                } as React.CSSProperties
            }
        />
    );
};

export { GitDiffView };
